import { buildStatCardGrid, buildStatValueGrid, isStatCardSection } from './template-rich-card/index.js';

const ICON_MAIN_CLASS = 'content__icon--main';
const ICON_SMALL_CLASS = 'content__icon--small';
const STRUCTURED_CLASS = 'template__rich--structured';
const RESULTS_CLASS = 'template__rich--results';
const INIT_ANIMATE_SELECTOR = '[data-animate-init]';
const VISIBLE_CLASS = 'is-visible';
const MIN_STAT_CARD_ROWS = 2;
// Webflow's Rich Text editor always leaves a trailing empty paragraph
// (often containing only a zero-width joiner, U+200D) as a cursor
// placeholder. It carries no content, so it's dropped before any
// structuring logic runs — otherwise it pads out the last row's body
// and breaks the stat-card shape check.
const EMPTY_TEXT_PATTERN = new RegExp('^[\\s\\u200B\\u200C\\u200D\\uFEFF]*$');

const isEmptyParagraph = (node) => node.matches('p') && EMPTY_TEXT_PATTERN.test(node.textContent);

const findFirstIcon = (root) =>
  root.querySelector(`svg.${ICON_MAIN_CLASS}`) ||
  root.querySelector(`svg.${ICON_SMALL_CLASS}`);

const findIconSource = (rich) => {
  const localIcon = findFirstIcon(rich);

  if (localIcon) {
    return localIcon;
  }

  let sibling = rich.previousElementSibling;

  while (sibling) {
    const siblingIcon = findFirstIcon(sibling);

    if (siblingIcon) {
      return siblingIcon;
    }

    sibling = sibling.previousElementSibling;
  }

  const section = rich.closest('.section');
  const sectionIcon = section ? findFirstIcon(section) : null;

  if (sectionIcon) {
    return sectionIcon;
  }

  const container = rich.closest('.container');

  return container ? findFirstIcon(container) : null;
};

const prepareIcon = (iconSource, rich) => {
  const icon = rich.contains(iconSource) ? iconSource : iconSource.cloneNode(true);

  icon.classList.remove(ICON_SMALL_CLASS);
  icon.classList.add(ICON_MAIN_CLASS);

  return icon;
};

const createColumn = (modifierClass) => {
  const column = document.createElement('div');

  column.className = modifierClass
    ? `content__column ${modifierClass}`
    : 'content__column';

  return column;
};

const createRow = (isIntro = false) => {
  const row = document.createElement('div');

  row.className = isIntro ? 'content__row no-border' : 'content__row';

  return row;
};

const appendLooseContent = (fragment, items) => {
  if (!items.length) {
    return;
  }

  const row = createRow();
  const leftColumn = createColumn();
  const rightColumn = createColumn('content__column--txt');

  items.forEach((item) => {
    rightColumn.appendChild(item);
  });

  row.appendChild(leftColumn);
  row.appendChild(rightColumn);
  fragment.appendChild(row);
};

const appendIntroRow = (fragment, rich, title) => {
  const row = createRow(true);
  const iconColumn = createColumn();
  const titleColumn = createColumn('content__column--txt');
  const iconSource = findIconSource(rich);

  if (iconSource) {
    iconColumn.appendChild(prepareIcon(iconSource, rich));
  }

  titleColumn.appendChild(title);
  row.appendChild(iconColumn);
  row.appendChild(titleColumn);
  fragment.appendChild(row);
};

const splitSections = (items, sectionSelector) => {
  const sections = [];
  let index = 0;

  while (index < items.length) {
    if (!items[index].matches(sectionSelector)) {
      const looseItems = [];

      while (index < items.length && !items[index].matches(sectionSelector)) {
        looseItems.push(items[index]);
        index += 1;
      }

      sections.push({ heading: null, body: looseItems });
      continue;
    }

    const heading = items[index];
    const body = [];
    index += 1;

    while (index < items.length && !items[index].matches(sectionSelector)) {
      body.push(items[index]);
      index += 1;
    }

    sections.push({ heading, body });
  }

  return sections;
};

const appendSectionRow = (fragment, section) => {
  if (!section.heading) {
    appendLooseContent(fragment, section.body);
    return;
  }

  const row = createRow();
  const headingColumn = createColumn();
  const bodyColumn = createColumn('content__column--txt');

  headingColumn.appendChild(section.heading);
  section.body.forEach((node) => bodyColumn.appendChild(node));

  row.appendChild(headingColumn);
  row.appendChild(bodyColumn);
  fragment.appendChild(row);
};

// Length of the maximal run of qualifying stat-card rows starting at
// `start` (exclusive end index). Stops at the first row that isn't a
// label/value H4 row, or at a heading-less row (which never qualifies).
const findValueRunEnd = (rows, start) => {
  let end = start;

  while (end < rows.length && isStatCardSection(rows[end])) {
    end += 1;
  }

  return end;
};

const isLoneH3 = (body) => body.length === 1 && body[0].matches('h3');

// Only an H2 and an H4 ever create a `content__row`. H3 is never a
// structural heading and never gets a row of its own — when it sits as
// the sole, immediate predecessor of a qualifying stat grid, it stays in
// the right column immediately before the generated card.
const buildH2Section = (fragment, rich, section) => {
  appendIntroRow(fragment, rich, section.heading);

  const sectionSelector = section.body.some((child) => child.matches('h4')) ? 'h4' : null;

  if (!sectionSelector) {
    appendLooseContent(fragment, section.body);
    return;
  }

  const rows = splitSections(section.body, sectionSelector);
  let index = 0;

  while (index < rows.length) {
    const row = rows[index];

    if (!row.heading) {
      // Leading content before the first H4. A trailing H3 belongs in
      // the same right column as the generated card when a qualifying
      // run follows it; otherwise it remains normal loose content.
      const lastLooseChild = row.body[row.body.length - 1];
      const trailingLooseH3 = lastLooseChild && lastLooseChild.matches('h3') ? lastLooseChild : null;

      if (trailingLooseH3) {
        const runEnd = findValueRunEnd(rows, index + 1);

        if (runEnd - (index + 1) >= MIN_STAT_CARD_ROWS) {
          appendLooseContent(fragment, row.body.slice(0, -1));
          appendLooseContent(fragment, [trailingLooseH3, buildStatCardGrid(rows.slice(index + 1, runEnd))]);
          index = runEnd;
          continue;
        }

        if (isLoneH3(row.body) && index + 1 < rows.length) {
          // Not followed by a qualifying run — fold the H3 into the next
          // row's body instead of giving it a row of its own.
          rows[index + 1].body.unshift(trailingLooseH3);
          index += 1;
          continue;
        }
      }

      appendLooseContent(fragment, row.body);
      index += 1;
      continue;
    }

    const nextRunEnd = findValueRunEnd(rows, index + 1);

    if (!row.body.length && nextRunEnd - (index + 1) >= MIN_STAT_CARD_ROWS) {
      appendSectionRow(fragment, {
        heading: row.heading,
        body: [buildStatCardGrid(rows.slice(index + 1, nextRunEnd))],
      });
      index = nextRunEnd;
      continue;
    }

    // An H4 row whose body ends in an H3 can hand that H3 to the
    // generated grid row, where it stays before the card in the right
    // column. If the following run doesn't qualify, the H3 remains
    // ordinary trailing content of this row.
    const lastChild = row.body[row.body.length - 1];
    const trailingH3 = lastChild && lastChild.matches('h3') ? lastChild : null;

    if (trailingH3) {
      const runEnd = findValueRunEnd(rows, index + 1);

      if (runEnd - (index + 1) >= MIN_STAT_CARD_ROWS) {
        appendSectionRow(fragment, { heading: row.heading, body: row.body.slice(0, -1) });
        appendLooseContent(fragment, [trailingH3, buildStatCardGrid(rows.slice(index + 1, runEnd))]);
        index = runEnd;
        continue;
      }
    }

    // No title situation applies here — check whether this row itself
    // starts an untitled qualifying run.
    const runEnd = findValueRunEnd(rows, index);

    if (runEnd - index >= MIN_STAT_CARD_ROWS) {
      appendLooseContent(fragment, [buildStatCardGrid(rows.slice(index, runEnd))]);
      index = runEnd;
      continue;
    }

    appendSectionRow(fragment, row);
    index += 1;
  }
};

const revealInitAnimationTarget = (rich) => {
  const target = rich.matches(INIT_ANIMATE_SELECTOR)
    ? rich
    : rich.closest(INIT_ANIMATE_SELECTOR);

  if (target) {
    target.classList.add(VISIBLE_CLASS);
  }
};

const structureResultsRichText = (rich, children) => {
  const rows = splitSections(children, 'h4');

  if (rows.length < MIN_STAT_CARD_ROWS || !rows.every(isStatCardSection)) {
    return false;
  }

  rich.textContent = '';
  rich.classList.add(STRUCTURED_CLASS);
  rich.dataset.templateRichStructured = 'true';
  rich.appendChild(buildStatValueGrid(rows));
  revealInitAnimationTarget(rich);

  return true;
};

const structureRichText = (rich) => {
  if (rich.dataset.templateRichStructured === 'true') {
    return;
  }

  const children = Array.from(rich.children).filter((child) => !isEmptyParagraph(child));

  if (rich.classList.contains(RESULTS_CLASS) && structureResultsRichText(rich, children)) {
    return;
  }

  if (!children.some((child) => child.matches('h2'))) {
    return;
  }

  const fragment = document.createDocumentFragment();
  const h2Sections = splitSections(children, 'h2');

  h2Sections.forEach((section) => {
    if (!section.heading) {
      // Content before the first H2 — discouraged (see skill rules), but
      // preserved rather than dropped.
      appendLooseContent(fragment, section.body);
      return;
    }

    buildH2Section(fragment, rich, section);
  });

  rich.textContent = '';
  rich.classList.add(STRUCTURED_CLASS);
  rich.dataset.templateRichStructured = 'true';
  rich.appendChild(fragment);
  revealInitAnimationTarget(rich);
};

export const initTemplateRichLayouts = (richTexts) => {
  richTexts.forEach(structureRichText);
};
